import React, { useMemo, useState } from 'react';
import './SeatingMap.css';

const SeatingMap = ({ seatingData, selectedSeats, onSeatSelect, seatStatuses }) => {
    const [hoveredSeat, setHoveredSeat] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipContent, setTooltipContent] = useState('');

    const { rows, maxX, maxY } = useMemo(() => {
        if (!seatingData?.seat_map) return { rows: {}, maxX: 0, maxY: 0 };

        // First pass: Group seats by row and find max coordinates
        const rowGroups = {};
        let maxX = 0;
        let maxY = 0;

        seatingData.seat_map.forEach(seat => {
            const [x, y] = seat.coordinates;
            const labelData = JSON.parse(seat.row.label);
            const rowLabel = labelData.row_label;
            
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);

            if (!rowGroups[rowLabel]) {
                rowGroups[rowLabel] = {
                    y: y,
                    seats: [],
                    coordinates: new Set()
                };
            }
            rowGroups[rowLabel].seats.push(seat);
            rowGroups[rowLabel].coordinates.add(x);
        });

        // Second pass: Fill in blank seats
        const processedRows = {};
        Object.entries(rowGroups).forEach(([rowLabel, rowData]) => {
            const { seats, coordinates, y } = rowData;
            const fullRow = [];
            
            // Sort seats by x coordinate
            seats.sort((a, b) => a.coordinates[0] - b.coordinates[0]);

            // Fill the row with seats and blanks
            for (let x = 0; x <= maxX; x++) {
                if (coordinates.has(x)) {
                    const seat = seats.find(s => s.coordinates[0] === x);
                    fullRow.push(seat);
                } else {
                    // Add a blank seat
                    fullRow.push({
                        row: {
                            label: JSON.stringify({
                                row_label: rowLabel,
                                seats: [{
                                    position: x + 1,
                                    number: null
                                }]
                            }),
                            status: 'blank'
                        },
                        coordinates: [x, y],
                        price_category: null,
                        isBlank: true
                    });
                }
            }
            
            processedRows[rowLabel] = {
                y: y,
                seats: fullRow
            };
        });

        return {
            rows: processedRows,
            maxX,
            maxY
        };
    }, [seatingData]);

    const sortedRows = useMemo(() => {
        return Object.entries(rows)
            .sort(([, a], [, b]) => a.y - b.y)
            .map(([label, row]) => ({
                label,
                ...row
            }));
    }, [rows]);

    const renderSeat = (seat) => {
        if (!seat) return null;

        const labelData = JSON.parse(seat.row.label);
        const seatLabel = `${labelData.row_label}${labelData.seats[0].number || ''}`;
        
        // Check if seat is selected by comparing the label
        const isSelected = selectedSeats.some(s => {
            const selectedLabel = typeof s === 'string' ? s : s.label;
            return selectedLabel === seatLabel;
        });
        
        // Determine seat status
        let status;
        if (seat.isBlank) {
            status = 'blank';
        } else if (isSelected) {
            status = 'selected';
        } else if (seatStatuses[seatLabel] === 'unavailable' || seat.row.status === 'unavailable') {
            status = 'unavailable';
        } else if (seat.row.status === 'available') {
            status = 'available';
        } else {
            status = 'unavailable';
        }

        const seatClasses = `seat ${status}`;

        // Find ticket type for price
        const ticketType = seatingData?.ticket_types?.find(
            type => type.price_category === seat.price_category
        );

        const handleMouseEnter = (event) => {
            if (!seat.isBlank) {
                const rect = event.currentTarget.getBoundingClientRect();
                const x = rect.left + (rect.width / 2);
                const y = rect.top;
                
                setTooltipPosition({ x, y });
                setTooltipContent(
                    `<div>Koltuk: ${seatLabel}</div>
                     <div>Kategori: ${seat.price_category}</div>
                     ${ticketType ? `<div>Fiyat: ${ticketType.price} TL</div>` : ''}
                     <div>Durum: ${
                         status === 'selected' ? 'Seçili' :
                         status === 'available' ? 'Müsait' : 
                         'Müsait Değil'
                     }</div>`
                );
                setShowTooltip(true);
                setHoveredSeat(null); // Remove the old tooltip
            }
        };

        const handleMouseLeave = () => {
            setShowTooltip(false);
            setHoveredSeat(null);
        };

        const handleClick = () => {
            // Allow clicking if the seat is available or already selected
            if (!seat.isBlank && (status === 'available' || status === 'selected')) {
                onSeatSelect(seatLabel);
            }
        };

        return (
            <div
                key={`${seatLabel}-${seat.coordinates.join('-')}`}
                className={seatClasses}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    gridColumn: seat.coordinates[0] + 1,
                    // Update cursor style to show pointer for both available and selected seats
                    cursor: seat.isBlank || (status !== 'available' && status !== 'selected') ? 'default' : 'pointer'
                }}
            >
                {!seat.isBlank && labelData.seats[0].number}
            </div>
        );
    };

    return (
        <div className="seating-map-container">
            <div className="seating-map">
                {sortedRows.map((row) => (
                    <div 
                        key={row.label} 
                        className="row"
                        style={{
                            gridRow: row.y + 1,
                            gridTemplateColumns: `repeat(${maxX + 1}, 1fr)`
                        }}
                    >
                        <div className="row-label">{row.label}</div>
                        {row.seats.map(seat => renderSeat(seat))}
                    </div>
                ))}
            </div>
            <div className="scroll-indicator">Kaydırarak tüm koltukları görebilirsiniz</div>
            {showTooltip && (
                <div 
                    className="seat-tooltip"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltipContent }}
                />
            )}
        </div>
    );
};

export default SeatingMap; 